/**
 * DocumentTypeCreateBadge — klikalna plakietka "(KOD)" w nagłówku formularza
 * NOWEGO dokumentu (wszystkie 7 klas). Pokazuje skrót aktualnie wybranego typu
 * i otwiera istniejący `DocumentTypePickerModal`, żeby dało się zmienić typ
 * bez wychodzenia z formularza — zanim dokument zostanie zapisany (spotkanie
 * z Dariuszem 2026-07-14, D.7+D.8 krok 1). Na już zapisanym dokumencie NIE jest
 * używana — ten przypadek (zmiana typu z renumeracją, D.8 krok 2) obsługuje
 * osobny komponent `DocumentTypeChangeBadge`.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';

import { DocumentTypePickerModal } from '@/components/documents/DocumentTypePickerModal';
import { type DocumentClass, useDocumentTypes } from '@/lib/use-document-types';

interface Props {
  docClass: DocumentClass;
  /** Aktualnie wybrany typ (z URL, patrz `effectiveDocumentTypeId` na stronach detalu). */
  documentTypeId: string | null;
  /** Firma, w kontekście której tworzony jest dokument (z URL, patrz `createCompanyId`). */
  companyId: string | null;
  onChange: (documentTypeId: string, companyId: string) => void;
  testId: string;
}

export function DocumentTypeCreateBadge({
  docClass,
  documentTypeId,
  companyId,
  onChange,
  testId,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data: types } = useDocumentTypes(docClass, { companyId });
  const selected = types.find((dt) => dt.id === documentTypeId);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="ml-1.5 inline-flex items-center gap-1 rounded-full border border-border-primary bg-bg-secondary px-2 py-0.5 align-middle text-base font-normal text-accent-600 hover:border-accent-500 hover:bg-accent-500/10"
        data-testid={testId}
        aria-label={t('tenant.documents.typePicker.changeTypeAria')}
        title={t('tenant.documents.typePicker.changeTypeAria')}
      >
        ({selected?.code ?? '…'})
        <Pencil className="h-3 w-3" aria-hidden="true" />
      </button>
      <DocumentTypePickerModal
        open={open}
        docClass={docClass}
        initialCompanyId={companyId}
        initialDocumentTypeId={documentTypeId}
        onSelect={(newTypeId, newCompanyId) => {
          onChange(newTypeId, newCompanyId);
          setOpen(false);
        }}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
